/**
 * Mirror of the code registry in `@elios-backend/common` (`FEATURE_FLAG_REGISTRY`, also mirrored
 * by iOS and Android). A gate exists only where code checks it, so adding a key is a code change
 * in common first — then here, so the console can show and write it. The backend rejects a write
 * for a key it does not know (400) and is the authority on every value below; the console uses
 * them for display and UX gating only.
 */

/** Registry keys, in the order the console lists them. */
export type FeatureFlagKey =
  | "emailPasswordSignIn"
  | "emailPasswordSignUp"
  | "cardIssuance"
  | "cardSensitiveDetails"
  | "cardPushProvisioning"
  | "kyc"
  | "crossChainSend"
  | "crossChainReceive"
  | "onramp"
  | "offramp"
  | "earnDeposits"
  | "walletMigrationStart";

/** The third parties whose maintenance windows a key depends on ("Pause vendor X"). */
export type FeatureFlagVendor = "rain" | "relay" | "hyperlend" | "meawallet";

/**
 * Which direction of change needs `superAdmin`, computed on availability (the set of builds that
 * evaluate available, per platform, plus the unknown-client outcome): `disable` = any write that
 * takes builds away, `enable` = any write that adds builds, `null` = any admin either way.
 */
export type FeatureFlagRiskyDirection = "enable" | "disable" | null;

/**
 * The apps' own default for a key with no row, where it differs from the server default — only
 * the two UI-only email keys (CONTRACT §1). Users see this, not `defaultAvailable`, until a row
 * exists; the first write replaces it on every bundle.
 */
export interface FeatureFlagAppDefault {
  /** Operator copy, e.g. "on in every bundle". */
  label: string;
  /**
   * The availability when it is the same on every bundle — a create starts from it, so saving it
   * unchanged changes nothing for users. Null when it depends on the bundle.
   */
  available: boolean | null;
  /** What turning the key off costs users once a row overrides the apps' default. */
  offImpact: string;
}

export interface FeatureFlagDefinition {
  /** What the key gates, in operator terms. */
  description: string;
  /**
   * Server code default when the key has no row: what the API evaluates, and the "before" of a
   * create for the superAdmin rule.
   */
  defaultAvailable: boolean;
  /** The apps' own default when it differs from `defaultAvailable`; null = they use it as is. */
  appDefault: FeatureFlagAppDefault | null;
  riskyDirection: FeatureFlagRiskyDirection;
  vendors: readonly FeatureFlagVendor[];
}

export const FEATURE_FLAG_REGISTRY: Readonly<Record<FeatureFlagKey, FeatureFlagDefinition>> = {
  emailPasswordSignIn: {
    description: '"Sign in with email" on the login screen (UI only).',
    defaultAvailable: false,
    appDefault: {
      label: "on in every bundle",
      available: true,
      offImpact:
        'Off hides "Sign in with email" on those builds: password-only accounts, the store-review accounts included, lose their way in.',
    },
    riskyDirection: null,
    vendors: [],
  },
  emailPasswordSignUp: {
    description: '"Create account" toggle on the email form (UI only).',
    defaultAvailable: false,
    appDefault: {
      label: "on in the .staging bundle only",
      available: null,
      offImpact: 'Off hides "Create account" on the .staging bundle too.',
    },
    riskyDirection: "enable",
    vendors: [],
  },
  cardIssuance: {
    description: "Ordering a new card.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["rain"],
  },
  cardSensitiveDetails: {
    description: "Revealing card number/CVV, revealing and changing the PIN.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["rain"],
  },
  cardPushProvisioning: {
    description: "Adding the card to Apple Wallet / Google Wallet.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["rain", "meawallet"],
  },
  kyc: {
    description: "Starting identity verification.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["rain"],
  },
  crossChainSend: {
    description: "Sending USDC to another chain (quote + commit).",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: "disable",
    vendors: ["relay"],
  },
  crossChainReceive: {
    description: "Creating cross-chain deposit addresses.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["relay"],
  },
  onramp: {
    description: "Opening a USD virtual account (bank deposits).",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["rain"],
  },
  offramp: {
    description: "Adding a payout bank account and withdrawing to a bank.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: "disable",
    vendors: ["rain"],
  },
  earnDeposits: {
    description: "Depositing into Earn vaults and approving a vault.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: null,
    vendors: ["hyperlend"],
  },
  // The wallet-migration kill switch (common 0.7.0, identity ≥ 0.17.1). No vendor, so no vendor
  // Pause / Resume ever writes it. Its sibling `walletMigrationCardGate` is an enforcement switch
  // ("available" = decline stale-generation cards) and is not mirrored until the console models
  // that kind; it stays readable as an unknown key meanwhile.
  walletMigrationStart: {
    description:
      "Starting a wallet upgrade or key rotation, and re-arming its transfer approvals before any money moves. Never stops a migration already moving.",
    defaultAvailable: true,
    appDefault: null,
    riskyDirection: "enable",
    vendors: [],
  },
};

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAG_REGISTRY) as FeatureFlagKey[];

export function isFeatureFlagKey(value: string): value is FeatureFlagKey {
  return Object.prototype.hasOwnProperty.call(FEATURE_FLAG_REGISTRY, value);
}

export const FEATURE_FLAG_VENDORS: readonly FeatureFlagVendor[] = [
  "rain",
  "relay",
  "hyperlend",
  "meawallet",
];

export const VENDOR_LABELS: Record<FeatureFlagVendor, string> = {
  rain: "Rain",
  relay: "Relay",
  hyperlend: "HyperLend",
  meawallet: "MeaWallet",
};

/** The registry keys tagged with a vendor, in registry order. */
export function keysForVendor(vendor: FeatureFlagVendor): FeatureFlagKey[] {
  return FEATURE_FLAG_KEYS.filter((key) => FEATURE_FLAG_REGISTRY[key].vendors.includes(vendor));
}
